import React, { ChangeEvent, useState } from 'react';
import { Select } from 'antd';
import { t } from '../../../../i18n';
import InputFilterItem from "../../components/InputFilterItem";
import { useTranslation } from 'react-i18next';
import { useFilterContainerClassName, useFilterInputContainerClassName } from '../../../../utils/languageUtils';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';

export interface AccountsFilterState {
    name: string;
    phone: string;
    email: string;
    vehiclePlate: string;
    dspId?: string;
    verificationStatus?: number;
    idDocumentType?: number;
    idDocumentNumber?: string;
    isBlacklist?: boolean;
}

interface AccountFilterProps {
    filters?: AccountsFilterState; // 外部传入的 filters，如果未传入则使用内部状态
    setFilters?: (filters: AccountsFilterState) => void; // 外部传入的 setFilters，如果未传入则使用内部状态
    handleFilter: (filters: AccountsFilterState) => void;
    handleResetFilters?: () => void; // 外部传入的重置函数，如果未传入则使用内部逻辑
    // 新增：DSP 下拉相关
    showDspFilter?: boolean;
    dspOptions?: Array<{ value: string; label: string; dspCode: string }>;
    dspLoading?: boolean;
}

const AccountsFilter: React.FC<AccountFilterProps> = ({
    filters: externalFilters,
    setFilters: externalSetFilters,
    handleFilter,
    handleResetFilters: externalHandleResetFilters,
    showDspFilter = false,
    dspOptions = [],
    dspLoading = false
}) => {

    // 使用统一的样式 Hook
    const containerClassName = useFilterContainerClassName();
    const inputContainerClassName = useFilterInputContainerClassName();
    const col1Labels = [
        t('delivery.personnel.label.name'),
        t('delivery.personnel.label.phone'),
        t('delivery.personnel.label.company'),
    ];
    const col2Labels = [
        t('delivery.personnel.label.email'),
        t('delivery.personnel.label.vehiclePlate'),
        t('delivery.driver.verificationStatus'),
    ];
    const col3Labels = [
        t('delivery.driver.idDocumentType'),
        t('delivery.driver.idDocumentNumber'),
        t('delivery.driver.accountStatus'),
    ];
    const col1SpanClassName = getSmartLabelClassName(col1Labels);
    const col2SpanClassName = getSmartLabelClassName(col2Labels);
    const col3SpanClassName = getSmartLabelClassName(col3Labels);

    // 使用外部传入的 filters 或内部状态
    const [internalFilters, setInternalFilters] = useState<AccountsFilterState>({
        name: '',
        phone: '',
        email: '',
        vehiclePlate: '',
        dspId: undefined,
        verificationStatus: undefined,
        idDocumentType: undefined,
        idDocumentNumber: '',
        isBlacklist: false
    });

    const filters = externalFilters ?? internalFilters;
    const setFilters = externalSetFilters ?? setInternalFilters;

    // 重置筛选条件
    const handleResetFilters = () => {
        const defaultFilters = {
            name: '',
            phone: '',
            email: '',
            vehiclePlate: '',
            dspId: undefined,
            verificationStatus: undefined,
            idDocumentType: undefined,
            idDocumentNumber: '',
            isBlacklist: false
        };
        setFilters(defaultFilters);
        if (externalHandleResetFilters) {
            externalHandleResetFilters();
        } else {
            handleFilter(defaultFilters);
        }
    };

    // 获取审核状态选项
    const getVerificationStatusOptions = () => [
        { label: t('delivery.driver.verificationStatusNotReady'), value: 0 },
        { label: t('delivery.driver.verificationStatusPending'), value: 1 },
        { label: t('delivery.driver.verificationStatusApproved'), value: 2 },
        { label: t('delivery.driver.verificationStatusRejected'), value: 3 },
    ];

    // 获取证件类型选项
    const getIdDocumentTypeOptions = () => [
        { label: t('delivery.driver.driverLicense'), value: 0 },
        // { label: t('delivery.driver.idCard'), value: 1 },
        // { label: t('delivery.driver.passport'), value: 2 },
        // { label: t('delivery.driver.ead'), value: 3 },
    ];


    // 获取账号状态选项
    const getAccountStatusOptions = () => [
        { label: t('delivery.driver.accountStatusNormal'), value: false },
        { label: t('delivery.driver.accountStatusBanned'), value: true },
    ];


    return (
        <div className="mb-2 pl-0 py-2">
            {/* 使用 Grid 布局确保列对齐 - 三等分布局 */}
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                {/* 第一列：姓名、电话、公司 */}
                <div className="min-w-0">
                    <InputFilterItem
                        label={t('delivery.personnel.label.name')}
                        value={filters.name}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, name: e.target.value })}
                        spanClassName={col1SpanClassName}
                    />
                </div>
                {/* 第二列：邮箱、车牌、审核状态 */}
                <div className="min-w-0">
                    <InputFilterItem
                        label={t('delivery.personnel.label.email')}
                        value={filters.email}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, email: e.target.value })}
                        spanClassName={col2SpanClassName}
                    />
                </div>
                {/* 第三列：证件类型、证件编号、空 */}
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={col3SpanClassName}>
                            {t('delivery.driver.idDocumentType')}
                        </span>
                        <div className={inputContainerClassName}>
                            <Select
                                placeholder={t('delivery.driver.filterByIdDocumentType')}
                                value={filters.idDocumentType}
                                options={getIdDocumentTypeOptions()}
                                onChange={(value) => setFilters({ ...filters, idDocumentType: value })}
                                allowClear
                                size="middle"
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>

                {/* 第二行 */}
                <div className="min-w-0">
                    <InputFilterItem
                        label={t('delivery.personnel.label.phone')}
                        value={filters.phone}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, phone: e.target.value })}
                        spanClassName={col1SpanClassName}
                    />
                </div>
                <div className="min-w-0">
                    <InputFilterItem
                        label={t('delivery.personnel.label.vehiclePlate')}
                        value={filters.vehiclePlate}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, vehiclePlate: e.target.value })}
                        spanClassName={col2SpanClassName}
                    />
                </div>
                <div className="min-w-0">
                    <InputFilterItem
                        label={t('delivery.driver.idDocumentNumber')}
                        value={filters.idDocumentNumber || ''}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, idDocumentNumber: e.target.value })}
                        spanClassName={col3SpanClassName}
                    />
                </div>

                {/* 第三行 */}
                {showDspFilter ? (
                    <div className="min-w-0">
                        <div className={containerClassName}>
                            <span className={col1SpanClassName}>
                                {t('delivery.personnel.label.company')}
                            </span>
                            <div className={inputContainerClassName}>
                                <Select
                                    placeholder={t('components.input.select')}
                                    value={filters.dspId ?? undefined}
                                    loading={dspLoading}
                                    options={dspOptions}
                                    onChange={(value) => setFilters({ ...filters, dspId: value })}
                                    allowClear
                                    size="middle"
                                    className="w-full"
                                    showSearch
                                    filterOption={(input, option) =>
                                        String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="min-w-0"></div>
                )}
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={col2SpanClassName}>
                            {t('delivery.driver.verificationStatus')}
                        </span>
                        <div className={inputContainerClassName}>
                            <Select
                                placeholder={t('delivery.driver.filterByVerificationStatus')}
                                value={filters.verificationStatus}
                                options={getVerificationStatusOptions()}
                                onChange={(value) => setFilters({ ...filters, verificationStatus: value })}
                                allowClear
                                size="middle"
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={col3SpanClassName}>
                            {t('delivery.driver.isBlacklist')}
                        </span>
                        <div className={inputContainerClassName}>
                            <Select
                                placeholder={t('delivery.driver.filterByAccountStatus')}
                                value={filters.isBlacklist}
                                options={getAccountStatusOptions()}
                                onChange={(value) => setFilters({ ...filters, isBlacklist: value })}
                                allowClear
                                size="middle"
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default AccountsFilter;